import { useEffect, useState } from "react";
import {
  fetchMaritimeQualifications,
  type MaritimeQualification,
  type QualificationType,
  type UserQualification,
} from "@/api/client";

export type MaritimeDepartment = "deck" | "engine";
export type MaritimeGrade = 1 | 2 | 3 | 4 | 5 | 6;

const VALID_DEPARTMENTS: readonly MaritimeDepartment[] = ["deck", "engine"];

export const isMaritimeDepartment = (value: string): value is MaritimeDepartment =>
  VALID_DEPARTMENTS.includes(value as MaritimeDepartment);

const DEPARTMENT_NAMES: Record<MaritimeDepartment, string> = {
  deck: "航海",
  engine: "機関",
};

const GRADE_NAMES: Record<MaritimeGrade, string> = {
  1: "一級",
  2: "二級",
  3: "三級",
  4: "四級",
  5: "五級",
  6: "六級",
};

// 科目の表示状態
export type SubjectDisplayState = {
  qualificationType: QualificationType;
  userQualification: UserQualification | null;
  status: "acquired" | "expired" | "not_acquired";
  isDisabled: boolean;
};

// 筆記合格判定の結果
export type WrittenExamStatus =
  | { passed: true; writtenQualification: UserQualification }
  | { passed: false; subjects: SubjectDisplayState[] };

type UseMaritimeQualificationsState = {
  maritimeQualifications: MaritimeQualification[];
  isLoading: boolean;
  error: Error | null;
};

export const useMaritimeQualifications = () => {
  const [state, setState] = useState<UseMaritimeQualificationsState>({
    maritimeQualifications: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchMaritimeQualifications();
        setState({ maritimeQualifications: data, isLoading: false, error: null });
      } catch (error) {
        setState({
          maritimeQualifications: [],
          isLoading: false,
          error:
            error instanceof Error ? error : new Error("Failed to fetch maritime qualifications"),
        });
      }
    };
    fetchData();
  }, []);

  // 部門と級から海技士資格を取得
  const getMaritimeQualification = (
    department: MaritimeDepartment,
    grade: MaritimeGrade,
  ): MaritimeQualification | null => {
    const departmentName = DEPARTMENT_NAMES[department];
    const gradeName = GRADE_NAMES[grade];
    const qualName = `海技士（${departmentName}）${gradeName}`;
    return state.maritimeQualifications.find((q) => q.name === qualName) ?? null;
  };

  // 筆記合格判定
  const checkWrittenExamStatus = (
    department: MaritimeDepartment,
    grade: MaritimeGrade,
    userQualificationMap: Map<number, UserQualification>,
  ): WrittenExamStatus | null => {
    const maritime = getMaritimeQualification(department, grade);
    if (!maritime?.writtenPass) return null;

    // 筆記合格のユーザー資格を確認
    const writtenUserQual = userQualificationMap.get(maritime.writtenPass.id);
    if (writtenUserQual && !writtenUserQual.isExpired) {
      return { passed: true, writtenQualification: writtenUserQual };
    }

    // 科目の状態を計算
    const subjects: SubjectDisplayState[] = maritime.subjects.map((subject) => {
      const userQual = userQualificationMap.get(subject.id) ?? null;
      let status: "acquired" | "expired" | "not_acquired";
      let isDisabled = false;

      if (userQual) {
        if (userQual.isExpired) {
          status = "expired";
        } else {
          status = "acquired";
          isDisabled = true;
        }
      } else {
        status = "not_acquired";
      }

      return {
        qualificationType: subject,
        userQualification: userQual,
        status,
        isDisabled,
      };
    });

    return { passed: false, subjects };
  };

  // 最上位資格のIDを取得（口述試験合格時に登録する資格）
  const getTopQualificationId = (
    department: MaritimeDepartment,
    grade: MaritimeGrade,
  ): number | null => {
    const maritime = getMaritimeQualification(department, grade);
    return maritime?.id ?? null;
  };

  // 最上位資格（口述試験合格）が取得済みかチェック
  const checkOralExamStatus = (
    department: MaritimeDepartment,
    grade: MaritimeGrade,
    userQualificationMap: Map<number, UserQualification>,
  ): { acquired: true; qualification: UserQualification } | { acquired: false } => {
    const maritime = getMaritimeQualification(department, grade);
    if (!maritime) return { acquired: false };

    const topQualification = userQualificationMap.get(maritime.id);
    if (topQualification?.isActive) {
      return { acquired: true, qualification: topQualification };
    }

    return { acquired: false };
  };

  return {
    ...state,
    getMaritimeQualification,
    checkWrittenExamStatus,
    checkOralExamStatus,
    getTopQualificationId,
    departmentNames: DEPARTMENT_NAMES,
    gradeNames: GRADE_NAMES,
  };
};
